import React from 'react'
import { Organisation, UserAccount } from '../../api/schemas/schema'
import { DataContext } from '../../context/DataContext'
import { useCallback, useContext, useEffect, useState } from 'react'
import { Avatar, Card, makeStyles, tokens, Text, mergeClasses } from '@fluentui/react-components'
import { t } from '../../i18n/i18n'
import { Grid } from '../common/grid/Grid'
import { GridItem } from '../common/grid/GridItem'
import { IOrganisationUserAccount } from '../../interfaces/IOrganisationUserAccount'
import { IOpenCloseModalProps } from '../../interfaces/IOpenCloseModalProps'
import { handleMessage } from '../../helpers/stateHelper'
import { Loader } from '../common/spinner/Loader'
import { api } from '../../helpers/apiHelper'
import { useNavigate } from 'react-router-dom'
import { DraggableModal } from '../common/modal/DraggableModal'

const useStyles = makeStyles({
   cardsWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingHorizontalL,
      padding: tokens.spacingVerticalM,
   },
   orgWrapper: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      boxShadow: tokens.shadow8,
   },
   orgDetails: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
   },
   modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: tokens.spacingVerticalM,
   },
   modalTitle: {
      margin: 0,
      fontSize: tokens.fontSizeBase500,
      fontWeight: tokens.fontWeightSemibold,
   },
   gridWrapper: {
      '@media screen and (min-width: 1007px)': {
         gap: tokens.spacingHorizontalXXL,
         minHeight: '200px',
         '& > :nth-last-child(1):nth-child(odd)': {
            gridColumn: 'span 12',
            justifySelf: 'center',
            minWidth: '50%',
         },
      },
   },
   activeOrg: {
      backgroundColor: tokens.colorBrandBackgroundInvertedSelected,
   },
})

export const ModalSwitchOrganisation = ({ open, closeModal }: IOpenCloseModalProps) => {
   const classes = useStyles()
   const navigate = useNavigate()

   const dataContext = useContext(DataContext)
   const { setRootState } = dataContext
   const { userAccount, organisation, hasMsalToken } = dataContext.state
   const [organisationUserAccount, setOrganisationUserAccount] = useState<IOrganisationUserAccount[]>([])
   useEffect(() => {
      if (!open) return
      const fetchData = async () => {
         try {
            const orgAndUserAcc = (await api().getAllOrganisationsByPersonalNumber(userAccount.user.personalNumber)) as unknown as {
               data: IOrganisationUserAccount[]
            }
            setOrganisationUserAccount(orgAndUserAcc.data)
         } catch (error) {
            handleMessage(setRootState, 'error', t('SomethingWentWrong'), t('CouldNotSwitchOrganisation'))
         }
      }

      fetchData()
   }, [hasMsalToken, open, setRootState, userAccount])

   const handleSwitchOrganisation = useCallback(
      async (selectedOrganisation: Organisation, selectedAccount: UserAccount) => {
         if (selectedAccount.organisationId !== selectedOrganisation.id) {
            handleMessage(setRootState, 'error', 'Wrong account for organisation')
            return
         }
         const currentOrg = dataContext.state.organisation
         const currentAcc = dataContext.state.userAccount
         if (currentOrg?.id === selectedOrganisation.id && currentAcc?.id === selectedAccount.id) return

         try {
            const response = await api().generateJwt(selectedOrganisation.id, selectedAccount.id)

            if (!response.ok) {
               if (response.status === 401 || response.status === 403) {
                  handleMessage(setRootState, 'error', t('NotAuthorized'), t('AccountNotInOrganisation'))
                  return
               }

               handleMessage(setRootState, 'error', t('SomethingWentWrong'), t('CouldNotSwitchOrganisation'))
               return
            }

            const userAccount = (await api().getMe()).data

            // Empty old organisation data in context to avoid old stale data and set new selected org and account
            setRootState((prev) => ({
               ...prev,
               organisation: userAccount?.organisationId ? { id: userAccount.organisationId } : null, // Organisation will be fetched from useEffect in DataContext when id is set
               userAccount: userAccount,
               defaultWorkplace: null,
               protectedAssetGoals: [],
               userAccounts: [],
            }))
            handleMessage(setRootState, 'success', t('SuccessfullySwitchedOrganisation'))
         } catch (error) {
            handleMessage(setRootState, 'error', t('SomethingWentWrong'), t('CouldNotSwitchOrganisation'))
         }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [organisation, userAccount, setRootState]
   )

   const handleOrganisationClick = (organisation: Organisation, account: UserAccount) => {
      handleSwitchOrganisation(organisation, account)

      navigate(`/`)
      closeModal()
   }

   return (
      <>
         <DraggableModal open={open} title={t('SelectOrganisation')} onClose={closeModal} noDivider>
            <div className={classes.cardsWrapper}>
               <Grid className={classes.gridWrapper}>
                  {organisationUserAccount.length === 0 ? (
                     <Loader />
                  ) : (
                     <>
                        {organisationUserAccount.map(({ key: org, value: account }) => (
                           <GridItem key={org.id} size={'6/12'}>
                              <Card
                                 onClick={() => handleOrganisationClick(org, account)}
                                 className={mergeClasses(classes.orgWrapper, account.id === userAccount.id ? classes.activeOrg : '')}
                              >
                                 <Avatar name={org.name} size={56} />
                                 <div className={classes.orgDetails}>
                                    <Text size={400} weight="bold">
                                       {org.name}
                                    </Text>
                                    <Text size={300}>{org.organisationNumber}</Text>
                                 </div>
                              </Card>
                           </GridItem>
                        ))}
                     </>
                  )}
               </Grid>
            </div>
         </DraggableModal>
      </>
   )
}
